// CybersecurityDataProtectionPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const CybersecurityDataProtectionPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/legal' },
    { name: 'Cybersecurity & Data Protection', path: '/legal/cybersecurity' },
  ];

  const securityMeasures = [
    {
      title: 'Robust Infrastructure Security',
      description: 'We employ industry-leading security measures to protect our infrastructure from cyber threats, including firewalls, intrusion detection systems, and regular vulnerability assessments.',
      details: [
        'State-of-the-art network security.',
        'Regular security audits and penetration testing.',
        'Redundant systems for disaster recovery.',
      ],
    },
    {
      title: 'Data Encryption',
      description: 'All sensitive data, both in transit and at rest, is encrypted using strong cryptographic protocols to prevent unauthorized access and ensure data confidentiality.',
      details: [
        'SSL/TLS encryption for all communications.',
        'Database encryption for sensitive information.',
        'Secure key management practices.',
      ],
    },
    {
      title: 'Access Control & Authentication',
      description: 'Strict access controls are in place to ensure that only authorized personnel can access sensitive systems and data. Multi-factor authentication is enforced for enhanced security.',
      details: [
        'Role-based access control (RBAC).',
        'Strong password policies and regular rotations.',
        'Continuous monitoring of access logs.',
      ],
    },
    {
      title: 'Incident Response Plan',
      description: 'We have a comprehensive incident response plan to quickly detect, respond to, and recover from any security incidents, minimizing potential impact.',
      details: [
        '24/7 security monitoring.',
        'Dedicated incident response team.',
        'Regular drills and training for staff.',
      ],
    },
    {
      title: 'User Education & Awareness',
      description: 'We empower our users with knowledge about cybersecurity best practices, helping them protect their own accounts and data from common online threats.',
      details: [
        'Tips for creating strong passwords.',
        'Awareness of phishing and social engineering attacks.',
        'Guidelines for secure platform usage.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Cybersecurity & Data Protection
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          The security of your data and transactions is our highest priority. We employ advanced cybersecurity measures and adhere to stringent data protection protocols to safeguard your information.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {securityMeasures.map((measure, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {measure.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {measure.description}
              </Typography>

              <List dense disablePadding>
                {measure.details.map((detail, detailIndex) => (
                  <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CybersecurityDataProtectionPage;
